import React, { memo, ReactNode, FC, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { TopNavContainer } from 'components/layout/TopNavContainer'
import { IconButton } from 'transfix-ui/components/IconButton'
import { makeStyles, Typography } from 'transfix-ui/core'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { LeftArrowIcon } from 'transfix-icons'

interface ITopNavProps {
  tag?: JSX.Element
  title: ReactNode
  inline?: ReactNode
  showBackButton?: boolean
  showMenu?: boolean
  showMenuIcon?: boolean
  includeTopNavDefaultClass?: boolean
  className?: string
  contentClassName?: string
  onPressBackButton?: () => void
}

const useStyles = makeStyles(theme => ({
  flexColumn: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  inline: {
    display: 'flex',
    flexDirection: 'column',
  },
}))

export const TopNav: FC<ITopNavProps> = memo<ITopNavProps>(
  ({
    children,
    className = '',
    contentClassName = '',
    includeTopNavDefaultClass,
    inline,
    onPressBackButton,
    showBackButton,
    showMenu = true,
    showMenuIcon,
    tag,
    title,
  }) => {
    const classes = useStyles()
    const { isMobile } = useBreakpointContext()
    const history = useHistory()

    const backClickHandler = useCallback(() => {
      if (onPressBackButton) {
        onPressBackButton()
      } else {
        history.goBack()
      }
    }, [history, onPressBackButton])

    return (
      <TopNavContainer
        showMenu={showMenu}
        showMenuIcon={showMenuIcon}
        includeTopNavDefaultClass={includeTopNavDefaultClass}
        className={className}
        contentClassName={contentClassName}>
        <div className={classes.flexColumn}>
          <div className='flex items-center'>
            {showBackButton && (
              <div className='pa0 ma0 db-l'>
                <IconButton
                  color='tertiary'
                  Icon={LeftArrowIcon}
                  onClick={backClickHandler}
                  label='Go back one page'
                  data-test='topnav-back-button'
                  size='large'
                />
              </div>
            )}
            <div className={`${showBackButton ? 'ml2-l' : 'ma0'}`}>
              <div className='flex flex-row items-center'>
                <Typography
                  variant={isMobile ? 'subtitle1' : 'h3'}
                  data-test='top-nav-title'
                  component='span'>
                  {title}
                </Typography>
                {tag && <span className='ml4'>{tag}</span>}
              </div>
            </div>
          </div>
          {inline && <div className={classes.inline}>{inline}</div>}
        </div>
        {children}
      </TopNavContainer>
    )
  }
)

export default TopNav
